//@flow
import {makeStyles, MenuItem, Typography} from '@material-ui/core';
import React, {useState} from 'react';
import {colors} from '../../assets/styles/colors';

import {useDebounceEffect} from '../../utils/react';
import CustomSelect from '../CustomSelect/CustomSelect';
import CustomTextField from '../CustomTextField/CustomTextField';

type Color = {
  name: string,
  hex: string,
};

type Props = {
  arrayTextColors: Color[],
  defaultTextColor: string,
  preview?: (textColor: string, bgColor: string) => React$Node,
  onChange?: (textColor: string, bgColor: string) => void,
  defaultBackgroundColor?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    width: '100%',
    height: '100%',
    padding: '.5rem .75rem',
    borderRadius: '1rem',
    gridTemplateRows: '1fr 1fr 1fr',
    border: `1px solid ${colors.grey300}`,
    gap: '.5rem',
    [theme.breakpoints.up('sm')]: {
      gridTemplateRows: '1fr',
      gridTemplateColumns: 'repeat(3, 1fr)',
      height: '130px',
      padding: '1rem 1.25rem',
      gap: 0,
    },
  },
  section: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',

    [theme.breakpoints.up('sm')]: {
      padding: '0 1rem',
      borderRight: `1px solid ${colors.grey300}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  sectionContent: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
  title: {
    color: colors.grey900,
    fontSize: '14px',
    fontWeight: 600,
  },
  subTitle: {
    color: colors.grey700,
    fontSize: '12px',
    fontWeight: 500,
  },

  colorPickerContainer: {
    display: 'flex',
    gap: '.25rem',
    alignItems: 'center',
  },
  colorLabel: {
    width: '48px',
    minWidth: '48px',
    height: '48px',
    borderRadius: '.25rem',
  },
  inputColor: {
    opacity: 0,
    width: 0,
    height: 0,
    position: 'relative',
  },
  textColorLabel: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
  },
  previewColor: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
  },
  textColorContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rectangle: {
    height: '100%',
    width: '100%',
    borderRadius: 4,
    padding: theme.spacing(2, 1.5),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  rectangleFirstText: {
    fontSize: '14px',
    fontWeight: 500,
    marginRight: theme.spacing(2),
    color: '#3A3A49',
  },
  rectangleSecondText: {
    fontWeight: 500,
    fontSize: '12px',
    color: '#3A3A49',
  },
}));

export default function TextAndBackgroundColorSelector(props: Props) {
  const {
    arrayTextColors,
    defaultTextColor,
    preview,
    onChange,
    defaultBackgroundColor,
  } = props;

  const [textColor, setTextColor] = useState(defaultTextColor);
  const [bgColor, setBgColor] = useState(
    defaultBackgroundColor || colors.primary
  );

  const classes = useStyles();

  useDebounceEffect(
    () => {
      onChange && onChange(textColor, bgColor);
    },
    750,
    [textColor, bgColor]
  );

  const handleChangeBgColor = (color: string) => {
    if (color.length <= 1) {
      return setBgColor(defaultBackgroundColor || colors.primary);
    }
    setBgColor(color);
  };

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <header className={classes.sectionHeader}>
          <Typography className={classes.title}>Color de fondo</Typography>
          <Typography className={classes.subTitle}>Código Hex</Typography>
        </header>

        <div className={classes.colorPickerContainer}>
          <label className={classes.colorLabel} style={{background: bgColor}}>
            <input
              type="color"
              className={classes.inputColor}
              value={bgColor}
              onInput={e => {
                handleChangeBgColor(e.target.value);
              }}
            />
          </label>

          <CustomTextField
            label=""
            value={bgColor}
            onChange={e => {
              handleChangeBgColor(e.target.value);
            }}
          />
        </div>
      </section>
      <section className={classes.section}>
        <header className={classes.sectionHeader}>
          <Typography className={classes.title}>Color de texto</Typography>
          <Typography className={classes.subTitle}>
            Selecciona un color
          </Typography>
        </header>

        <div className={classes.textColorContainer}>
          <CustomSelect
            fullWidth
            className={classes.select}
            value={textColor}
            onChange={e => {
              setTextColor(e.target.value);
            }}
          >
            {arrayTextColors.map((el, idx) => (
              <MenuItem
                key={'text-color-' + idx}
                value={el.hex}
                className={classes.itemSelectFontColor}
              >
                <div className={classes.textColorLabel}>
                  <div
                    className={classes.previewColor}
                    style={{background: el.hex}}
                  />

                  <Typography>{el.name}</Typography>
                </div>
              </MenuItem>
            ))}
          </CustomSelect>
        </div>
      </section>
      <section className={classes.section}>
        <header className={classes.sectionHeader}>
          <Typography className={classes.title}>Previsualización</Typography>
        </header>
        <div className={classes.sectionContent}>
          {preview ? (
            preview(textColor, bgColor)
          ) : (
            <div
              className={classes.rectangle}
              style={{backgroundColor: bgColor}}
            >
              <Typography
                className={classes.rectangleFirstText}
                style={{color: textColor}}
              >
                Aa
              </Typography>
              <Typography
                className={classes.rectangleSecondText}
                style={{color: textColor}}
              >
                Color de texto
              </Typography>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
